import { Component, OnInit } from '@angular/core';
import { MapPlace } from '../models/MapPlace';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MapV2Service } from '../mapv2.service';
import { Map, MapView } from '../models/Map';
import { Sector } from '../models/Sector';

@Component({
  selector: 'mapv2-places',
  templateUrl: './mapv2-places.component.html',
  styleUrls: ['./mapv2-places.component.scss']
})
export class MapV2PlacesComponent implements OnInit {
  public loading!: boolean;
  public sector!: Sector;
  public view!: MapView;
  public map!: Map;
  public selectedPlace?: MapPlace;
  public selectedPlaces!: MapPlace[];
  public amount: number;
  private zoom: number;
  private startX!: number;
  private startY!: number;
  private scrollStartX!: number;
  private scrollStartY!: number;
  private unsubscribe$: Subject<void>;

  constructor(
    private service: MapV2Service
  ) {
    this.unsubscribe$ = new Subject();
    this.zoom = 1;
    this.amount = 0;
  }

  public ngOnInit(): void {
    this.initializeLoading();
    this.initializeView();
    this.initializeMap();
    this.initializeSector();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.complete();
    this.unsubscribe$.next();
  }

  public get isTicketsShowing(): boolean {
    return this.view === MapView.TICKETS;
  }

  public get isCartShowing(): boolean {
    return this.view === MapView.CART;
  }

  public closePlaces(): void {
    this.service.hide();
  }

  public closeDrawer(): void {
    this.service.setView(MapView.PLACES);
  }

  public openTickets(place: MapPlace): void {
    if (!this.isDisabledPlace(place)) {
      this.selectedPlace = place;
      this.service.setView(MapView.TICKETS);
    }
  }

  public openCart(): void {
    this.service.setView(MapView.CART);
  }

  public getPlaceStyle(place: MapPlace): object {
    const [left, top] = place.coords.split(',');
    const borderRadius = place.shape == 'circle' ? '100%' : '1px';
    let backgroundColor: string;
    const isSelected = this.selectedPlaces.find((p) => (
      p.row == place.row && p.number == place.number
    ));
    if (isSelected) {
        backgroundColor = 'rgba(0, 0, 255, .8)';
    }
    else if (place.available) {
      backgroundColor = 'rgba(0, 255, 0, .8)';
    }
    else {
      backgroundColor = 'rgba(160, 160, 160, .8)';
    }
    return {
      top: `${top}px`,
      left: `${left}px`,
      height: `${place.height}px`,
      width: `${place.width}px`,
      borderRadius,
      backgroundColor
    }
  }

  public isDisabledPlace(place: MapPlace) {
    const isSelected = this.selectedPlaces.find((p) => (
      p.row == place.row && p.number == place.number
    ));
    return !place.available && !isSelected;
  }

  public zoomIn(): void {
    const zoomElement = document.querySelector('#image-zoom') as HTMLElement;
    this.zoom += 0.1;
    zoomElement.style['zoom'] = this.zoom.toString();
  }

  public zoomOut(): void {
    const zoomElement = document.querySelector('#image-zoom') as HTMLElement;
    if (this.zoom - 0.1 >= 1) {
      this.zoom -= 0.1;
    }
    zoomElement.style['zoom'] = this.zoom.toString();
  }

  public loadImageEvents() {
    const element = document.getElementById('image-zoom') as HTMLElement;

    element.addEventListener('mousedown', (e) => {
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.scrollStartX = element.scrollLeft;
      this.scrollStartY = element.scrollTop;

      const drag = (event: MouseEvent) => {
        const currentX = event.clientX;
        const currentY = event.clientY;
        const diffX = currentX - this.startX;
        const diffY = currentY - this.startY;
        
        element.scrollLeft = this.scrollStartX - diffX;
        element.scrollTop = this.scrollStartY - diffY;

        element.style.pointerEvents = 'none';
      }
  
      const stopDrag = () => {
        document.removeEventListener('mousemove', drag);
        document.removeEventListener('mouseup', stopDrag);

        element.style.pointerEvents = 'auto';
      }
      
      document.addEventListener('mousemove', drag);
      document.addEventListener('mouseup', stopDrag);
    });

    element.addEventListener('wheel', (event) => {
      event.preventDefault();
      const speed = 0.05;
      if (event.deltaY < 0) {
        this.zoom += speed;
      }
      else if (this.zoom - speed >= 1) {
        this.zoom -= speed;
      }
      element.style['zoom'] = this.zoom.toString();
    });
  }

  private initializeMap(): void {
    this.service.getMap()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (map) => {
          if (map) {
            this.map = map;
            this.selectedPlaces = map.places.filter(p => p.isReserved);
            this.amount = this.selectedPlaces.reduce((sum, place) => (
              sum + ((place.selectedTicket!.price + place.selectedTicket!.fee) * place.quantity)
            ), 0);
          }
        }
      });
  }

  private initializeSector(): void {
    this.service.getSelectedSector()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (sector) => {
          if (sector) {
            this.sector = sector;
          }
        }
      });
  }

  private initializeLoading(): void {
    this.service.isLoading()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (isLoading) => {
          this.loading = isLoading;
        }
      });
  }

  private initializeView(): void {
    this.service.getView()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (view) => {
          this.view = view;
        }
      });
  }
}
